import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

import wallpaperImage from '../../assets/images/home/wallpaper-image-04.jpg';

/**
 * PageTransition component - A single continuous image curtain that
 * moves up from the bottom, passes through the screen, and exits at the top.
 * @param {Object} props - Component props
 * @param {boolean} props.isAnimating - Animation state
 * @param {Function} props.onAnimationComplete - Callback when animation completes
 * @param {number} props.duration - Animation duration in seconds
 * @returns {React.Element} PageTransition component
 */
const PageTransition = ({ isAnimating, onAnimationComplete, duration }) => {
  // End animation after the total duration (failsafe)
  useEffect(() => {
    if (isAnimating) {
      const timeout = setTimeout(() => {
        onAnimationComplete('exit');
      }, duration * 1000);
      return () => clearTimeout(timeout);
    }
  }, [isAnimating, duration, onAnimationComplete]);

  // Move from bottom to top in one smooth motion, no pause
  const variants = {
    initial: { y: '100%' },
    animate: {
      y: '-100%',
      transition: {
        duration, // full duration for the whole movement
        ease: 'easeInOut'
      }
    },
    exit: { y: '-100%' } // stays out of view when unmounting
  };

  return (
    <AnimatePresence mode='wait'>
      {isAnimating && (
        <motion.div
          key='image-curtain'
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            pointerEvents: 'all',
            overflow: 'hidden',
            willChange: 'transform',
            background: '#111' // fallback while loading image
          }}
          onAnimationComplete={definition => {
            if (definition === 'animate' || definition === 'exit') {
              onAnimationComplete('exit');
            }
          }}
        >
          <img
            src={wallpaperImage}
            alt=''
            style={{
              width: '100vw',
              height: '100vh',
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              pointerEvents: 'none',
              userSelect: 'none'
            }}
            draggable={false}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

PageTransition.propTypes = {
  isAnimating: PropTypes.bool.isRequired,
  onAnimationComplete: PropTypes.func.isRequired,
  duration: PropTypes.number.isRequired
};

export default PageTransition;
